<template>
  <div
    ref="inputRef"
    class="phone"
    :class="classes"
  >
    <label class="phone__container">
      <div
        class="phone__label"
        v-html="autoLabel"
      />
      <vue-tel-input
        v-model="number"
        class="phone__wrapper"
        default-country="ch"
        mode="international"
        valid-characters-only
        :dropdown-options="{
          showFlags: true,
          showSearchBox: true,
        }"
        :input-options="{
          name: $props.name,
          showDialCode: true,
          placeholder: $props.label,
          showSearchBox: true,
          styleClasses: 'phone__input outline',
        }"
        :preferred-countries="['CH', 'DE', 'IT']"
        @validate="validate"
        @blur="onBlur"
        @focus="onFocus"
      >
        <template #arrow-icon>
          <the-icon
            art="light"
            name="chevron-down"
          />
        </template>
      </vue-tel-input>

      <div class="phone__outline" />

      <the-icon
        v-if="showInvalidIcon"
        art="solid"
        class="phone__invalid"
        name="circle-exclamation"
      />

      <the-icon
        v-if="showClearIcon"
        art="solid"
        class="phone__clear"
        name="circle-xmark"
        @mousedown.prevent="doClear"
      />
    </label>
    <div class="phone__line" />
    <div class="phone__supporting-text caption-regular">
      <span v-text="autoHint" />
    </div>
  </div>
</template>

<script setup>
  import { computed, ref, watch } from 'vue'
  import { VueTelInput } from 'vue-tel-input'

  import useI18n from '@shared/hooks/useI18n'

  import 'vue-tel-input/vue-tel-input.css'

  // HOOKS
  const { t } = useI18n()

  const model = defineModel({ type: String })
  const props = defineProps({
    label: {
      type: String,
      default: '',
    },

    name: {
      type: String,
      default: 'mobile',
    },

    supportingText: {
      type: String,
      default: null,
    },

    v: {
      type: Object,
      default: () => {},
    },
  })

  // DATA
  const isFocused = ref(false)
  const inputRef = ref()
  const validityState = ref({})
  const number = ref(null)

  // COMPUTED
  const autoHint = computed(() => {
    const error = props.v?.$errors[0]?.$validator
    if (error) return t(`form.error.${error}`)
    return props.supportingText
  })

  const autoLabel = computed(() => {
    let str = props.label
    if (isRequired.value) {
      str += ' *'
    }

    return str
  })

  const classes = computed(() => {
    return {
      'phone--invalid': isInvalid.value,
      'phone--focused': isFocused.value,
    }
  })

  const isInvalid = computed(() => {
    return props.v?.$dirty && props.v?.$invalid
  })

  const isRequired = computed(() => {
    return !!props.v?.required
  })

  const showClearIcon = computed(() => {
    return isFocused.value && number
  })

  const showInvalidIcon = computed(() => {
    return isInvalid.value && !isFocused.value
  })

  // METHODS
  function doClear() {
    number.value = null
  }

  function onBlur() {
    inputRef.value.querySelector('.phone__input ')?.classList.remove('has-outline')

    isFocused.value = false

    update()
  }

  function onFocus() {
    isFocused.value = true
  }

  function update() {
    model.value = validityState.value.valid ? number.value : null
    props.v?.$validate()
  }

  function validate(data) {
    let doUpdate = false

    if (validityState.value.valid !== undefined && validityState.value.valid !== data.valid) {
      doUpdate = true
    }

    validityState.value = data

    if (doUpdate) update()
  }

  // WATCHERS
  watch(
    model,
    value => {
      if (value && value !== number.value) {
        number.value = value
      }
    },
    { immediate: true }
  )
</script>

<style scoped>
.phone {
  --phone-active-indicator-color: var(--on-surface);

  width: 100%;
  margin: 0 0 var(--fixed-spacing-fix-04);
  color: var(--on-surface);
  position: relative;

  &&--focused {
    .phone__container:has(.has-outline) .phone__outline {
      outline: 2px solid var(--on-surface);
      outline-offset: var(--fixed-spacing-fix-01);
      border-radius: var(--fixed-border-radius-fix-02);
    }

    .phone__line {
      box-shadow: inset 0 -2px 0 var(--phone-active-indicator-color);
    }
  }

  &&--invalid {
    .phone__container .phone__wrapper :deep(.phone__input),
    .phone__container .phone__label,
    .phone__supporting-text {
      color: var(--error);
    }

    .phone__line {
      --phone-active-indicator-color: var(--error);
    }
  }

  .phone__container {
    position: relative;
    height: 48px;
    width: 100%;
    display: inline-block;

    .phone__label {
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      position: absolute;
      height: var(--spacing-san-spacing-06);
      top: 0;
      left: 68px;
      padding: 0 0 0 var(--spacing-san-spacing-04);
      width: calc(100% - 68px);
      font-size: var(--font-size-regular-xs);
      line-height: var(--line-height-regular-xs);
      letter-spacing: var(--letter-spacing-regular-xs);
      color: var(--dvp-input-c-label-text);
      transition-property: all;
      transition-timing-function: ease-in-out;
      transition-duration: 200ms;
      cursor: text;
      background-color: var(--Input-fields-input-on-default-surface-fill);
      border-radius: var(--fixed-border-radius-fix-02) var(--fixed-border-radius-fix-02) 0 0;
    }

    .phone__wrapper {
      border: none;
      outline: none;
      display: flex;
      gap: var(--fixed-spacing-fix-04);
      height: 48px;

      &:focus-within {
        box-shadow: none;
      }

      :deep(.vti__dropdown) {
        background-color: var(--Input-fields-input-on-default-surface-fill);
        border-radius: var(--fixed-border-radius-fix-02);
      }

      :deep(.phone__input) {
        background-color: var(--Input-fields-input-on-default-surface-fill);
        padding: 0 var(--fixed-spacing-fix-04);
        font-size: var(--font-size-regular-md);
        line-height: var(--line-height-regular-md);
        border-radius: 0 0 var(--fixed-border-radius-fix-02) var(--fixed-border-radius-fix-02);

        height: 24px;
        margin: 24px 0 0 0;
      }

      :deep(.vti__dropdown-list) {
        z-index: var(--dvp-stack-level-popup-menu);
        margin: var(--fixed-spacing-fix-04) 0 0;
        scrollbar-width: thin;
        scrollbar-color: var(--on-surface-medium) var(--on-primary);
      }

      :deep(.vti__search_box_container) {
        position: relative;
        margin: 4px;

        &:after {
          position: absolute;
          content: '';
          bottom: 1px;
          height: 2px;
          left: var(--fixed-spacing-fix-04);
          right: var(--fixed-spacing-fix-04);
          box-shadow: inset 0 -2px 0 var(--on-surface);
        }
      }

      :deep(.vti__search_box) {
        border-radius: var(--fixed-border-radius-fix-02);
        background-color: var(--Input-fields-input-on-default-surface-fill);
        width: 99%;
        color: var(--on-surface);
        border: 0;
        font-size: var(--font-size-regular-md);
        padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-02)
          var(--fixed-spacing-fix-04);
      }

      :deep(.vti__dropdown-item) {
        padding: var(--fixed-spacing-fix-02) var(--fixed-spacing-fix-06) var(--fixed-spacing-fix-02)
          var(--fixed-spacing-fix-04);
      }

      :deep(.vti__dropdown-item strong) {
        font-weight: normal;
      }

      :deep(.highlighted) {
        background-color: var(--Interaction-States-Dropdowns-hovered);
      }
    }

    .phone__outline {
      position: absolute;
      height: 48px;
      width: calc(100% - 68px);
      top: 0;
      left: 68px;
      cursor: text;
    }

    .phone__invalid {
      position: absolute;
      top: var(--spacing-san-spacing-04);
      right: var(--spacing-san-spacing-04);
      color: var(--error);
    }

    .phone__clear {
      position: absolute;
      top: var(--spacing-san-spacing-04);
      right: var(--spacing-san-spacing-04);
      cursor: pointer;
    }
  }

  .phone__line {
    position: absolute;
    height: 2px;
    bottom: var(--line-height-regular-xs);
    left: 84px;
    right: var(--fixed-spacing-fix-04);
    box-shadow: inset 0 -1px 0 var(--phone-active-indicator-color);
  }

  .phone__supporting-text {
    --input-indent-supporting-text: 85px;

    margin: 0 0 0 85px;
    color: var(--dvp-input-c-supporting-text);
    height: var(--line-height-regular-xs);
    margin: 0 0 0 var(--input-indent-supporting-text);

    & span {
      display: inline-block;
      width: calc(100% - var(--fixed-spacing-fix-04));
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}
</style>
